import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../../../../constants'
import { CreationToolSourceFile } from '../../../../types'
import CreationToolWatermarkPosition from '../../../../types/watermark'
import ArtboardComponent from '../../../artboard'
import Watermark from '../../../artboard/layer/watermark'
import Text from '../../../artboard/layer/text'
import VideoPlayerComponent from '../../../video-player'
import { SourceEditorCaption, SourceEditorCaptionStyle } from '../../../source-editor/types'
import CreationToolFont from '../../../../types/font'
import CreationToolFontSize from '../../../../types/font-size'
import { ArtboardDragPosition } from '../../../artboard/types'
import { useMemo } from 'react'

const Preview = styled.div`
    position: relative;
    text-align: center;
`

const VideoPlayer = styled(VideoPlayerComponent)`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    display: inline-block;
    width: auto;
`

const Artboard = styled(ArtboardComponent)`
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
    width: number
    watermark?: string
    watermarkPosition?: CreationToolWatermarkPosition
    defaultWatermarkWidth?: number
    onWatermarkResize?: (width: number) => void
    captionStyle?: SourceEditorCaptionStyle
    onCaptionMove: (pos: ArtboardDragPosition) => void
    autoCaption: boolean
}

const CreationCapturePreview = ({
    className,
    source,
    width,
    watermark,
    watermarkPosition = CreationToolWatermarkPosition.BottomRight,
    defaultWatermarkWidth,
    onWatermarkResize,
    captionStyle,
    onCaptionMove,
    autoCaption,
}: Props) => {
    const canvasScale = source.width / width
    const height = width / (source.width / source.height)
    let previewString = 'This is how captions will appear by default.'
    const { color, strokeColor, font, fontSize } = captionStyle || {}

    if (font && CreationToolFont.isAllCaps(font)) {
        previewString = previewString.toUpperCase()
    }

    const caption: SourceEditorCaption = useMemo(() => {
        return {
            id: source.id,
            text: previewString.trim(),
            x: width / 2,
            y: height * 0.9,
            rotation: 0,
            options: {},
            style: {
                color: color!,
                font: font!,
                fontSize: fontSize!,
            },
        }
    }, [font, width, height, previewString, color, fontSize])

    return (
        <Preview className={className}>
            <VideoPlayer source={source} width={width}>
                <Artboard
                    {...{
                        width,
                        height,
                        canvasScale,
                    }}
                >
                    {watermark && (
                        <Watermark
                            {...{
                                source: watermark,
                                position: watermarkPosition,
                                width: (defaultWatermarkWidth || Math.round(source.width * 0.2)) / canvasScale,
                                resizeable: true,
                                onUpdate: onWatermarkResize,
                            }}
                        />
                    )}
                    {captionStyle && autoCaption && (
                        <Text
                            defaultX={width / 2}
                            defaultY={height * 0.9}
                            resizeable={false}
                            rotateable={false}
                            moveable={true}
                            caption={caption}
                            text={previewString.trim()}
                            color={color!}
                            fontFamily={CreationToolFont.fontFamily(font!)}
                            fontSize={CreationToolFontSize.size(fontSize!) * CreationToolFont.scale(font!)}
                            strokeColor={strokeColor}
                            maxWidth={width}
                            fontWeight={CreationToolFont.fontWeight(font!)}
                            onMove={(pos) => {
                                onCaptionMove(pos)
                            }}
                        />
                    )}
                </Artboard>
            </VideoPlayer>
        </Preview>
    )
}

export default CreationCapturePreview
